import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "./lib/cn";

export type ToastKind = "success" | "error" | "info";

export interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

const ToastContext = createContext<ToastApi | null>(null);

const dotClasses: Record<ToastKind, string> = {
  success: "bg-success",
  error: "bg-danger",
  info: "bg-primary",
};

const borderClasses: Record<ToastKind, string> = {
  success: "border-success/30",
  error: "border-danger/30",
  info: "border-border-default",
};

export interface ToastProviderProps {
  children: ReactNode;
  /** Otomatik kapanma süresi (ms). */
  duration?: number;
}

export function ToastProvider({ children, duration = 4000 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const counter = useRef(0);
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending) clearTimeout(timer);
    };
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      const id = ++counter.current;
      setToasts((prev) => [...prev, { id, kind, message }]);
      /**
       * ⚠ HATA OTOMATİK KAPANMIYOR (CLOS-1194).
       *
       * Üç tür ayrılmıştı ama davranış ayrılmamıştı: bir başarı bildirimi ile kullanıcının
       * EYLEMİNİ ENGELLEYEN bir hata eşit yaşıyordu. Yerelde ölçüldü — mutabakat 403'ünün
       * metni 150 ms'de beliriyor, 3900 ms'de kayboluyordu; kullanıcı sebebi okumaya
       * yetişemiyordu ve "engelin sebebi söylenir" ilkesi fiilen çalışmıyordu.
       *
       * ⚠ Süreyi UZATMAK seçilmedi: "ne kadar uzun yeterli" sorusunun cevabı yok, okuma
       * hızı kullanıcıya göre değişir. Kapatma düğmesi zaten var; hatayı kullanıcı kapatıyor.
       */
      if (kind === "error") return;
      const timer = setTimeout(() => {
        timers.current.delete(timer);
        dismiss(id);
      }, duration);
      timers.current.add(timer);
    },
    [dismiss, duration],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push("success", message),
      error: (message) => push("error", message),
      info: (message) => push("info", message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        /*
          ⚠ DUYURU TÜRE GÖRE (CLOS-1194): kapsayıcı `polite` kalıyor — başarı ve bilgi
          ekran okuyucunun sözünü kesmemeli. Engelleyici hata ise `assertive`/`alert`:
          "boşta kalınca söyle" demek, kullanıcının yapamadığı işi ona geç bildirmekti.
        */
        <div aria-live="polite" className="fixed right-4 top-4 z-[60] flex w-80 flex-col gap-2">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              role={toast.kind === "error" ? "alert" : "status"}
              aria-live={toast.kind === "error" ? "assertive" : undefined}
              className={cn(
                "flex items-start gap-2 rounded-md border bg-surface p-3 text-sm text-fg shadow-md",
                borderClasses[toast.kind],
              )}
            >
              <span className={cn("mt-1 size-2 shrink-0 rounded-full", dotClasses[toast.kind])} />
              <p className="flex-1">{toast.message}</p>
              <button
                type="button"
                aria-label="Bildirimi kapat"
                onClick={() => dismiss(toast.id)}
                className="text-fg-faint transition-colors hover:text-fg-muted"
              >
                <svg viewBox="0 0 20 20" fill="currentColor" className="size-4" aria-hidden="true">
                  <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                </svg>
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast, ToastProvider içinde kullanılmalıdır");
  return ctx;
}
